import { useApp, useFetch } from '../store/app';
import { Fade } from './common';
import { Icon } from '../components/Icon';
import { Card, Button, Avatar, StatusBadge } from '../components/primitives';
import { st } from '../lib/status';
import { av } from '../lib/avatar';

interface DetailRow { label: string; value: string; icon: string }
interface MyInvoice { id: number; num: string; project_name: string; amount: string; status: string }
interface ProfileData {
  name: string;
  role: string;
  type: string;
  location: string;
  avatar_kind: string;
  details: DetailRow[];
  payment: DetailRow[];
  myInvoices: MyInvoice[];
}

export function Profile() {
  const { openDrawer } = useApp();
  const { data: p } = useFetch<ProfileData>('/profile');
  if (!p) return null;
  const a = av(p.name, p.avatar_kind);

  return (
    <Fade>
      <Card style={{ padding: 18, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Avatar initials={a.in} bg={a.bg} fg={a.fg} size={56} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <div style={{ fontSize: 19, fontWeight: 700 }}>{p.name}</div>
              <StatusBadge badge={st(p.type === 'FTE' ? 'fte' : 'contractor')} pill={false} />
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 2 }}>{p.role} · {p.location}</div>
          </div>
          <Button variant="secondary" icon="edit" onClick={() => openDrawer({ type: 'profile', mode: 'edit' })}>Edit profile</Button>
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
        <DetailCard title="Contact details" rows={p.details} />
        <DetailCard title="Payment & tax" rows={p.payment} />
      </div>

      <Card style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 600, fontSize: 14.5 }}>My invoices</div>
          <div style={{ flex: 1 }} />
          <Button icon="add" onClick={() => openDrawer({ type: 'invoice-new', mode: 'create' })} style={{ padding: '6px 12px' }}>Submit invoice</Button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr 1fr 1fr', gap: 14, padding: '10px 18px', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
          <div>Invoice</div><div>Project</div><div>Amount</div><div>Status</div>
        </div>
        {p.myInvoices.map((inv) => (
          <div key={inv.id} onClick={() => openDrawer({ type: 'invoice-view', mode: 'view', id: inv.id })} className="cc-hover-s2 cc-clickable" style={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr 1fr 1fr', gap: 14, padding: '12px 18px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
            <div className="cc-mono" style={{ fontSize: 13, fontWeight: 600 }}>{inv.num}</div>
            <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{inv.project_name}</div>
            <div className="cc-mono" style={{ fontSize: 13, fontWeight: 600 }}>{inv.amount}</div>
            <div><StatusBadge badge={st(inv.status)} /></div>
          </div>
        ))}
      </Card>
    </Fade>
  );
}

function DetailCard({ title, rows }: { title: string; rows: DetailRow[] }) {
  return (
    <Card style={{ padding: 18 }}>
      <div style={{ fontWeight: 600, fontSize: 14.5, marginBottom: 14 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {rows.map((r) => (
          <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 34, height: 34, borderRadius: 8, background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name={r.icon} size={18} color="var(--text-2)" /></div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{r.label}</div>
              <div style={{ fontSize: 13.5, fontWeight: 500 }}>{r.value}</div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
